const selectors = {
  container: '[data-marquee-banner]',
  measureElement: '[data-marquee-measure]',
  pathElement: '[data-marquee-path]',
  textPathElement: '[data-marquee-text-path]',
  tspans: '[data-marquee-tspan]',
};

/**
 * DynamicMarqueeBanner - Based on reference implementation
 * Measures text width and animates stroke-dashoffset and startOffset together
 */
export default class DynamicMarqueeBanner {
  constructor(section) {
    this.el = section.el;
    this.section = section;
    this.container = this.el.querySelector(selectors.container);
    this.measureElement = this.el.querySelector(selectors.measureElement);
    this.pathElement = this.el.querySelector(selectors.pathElement);
    this.textPathElement = this.el.querySelector(selectors.textPathElement);
    this.tspans = this.el.querySelectorAll(selectors.tspans);
    this.animationId = null;
    this.isPaused = false;
    this.segmentLength = 0; // Will be calculated from text measurement
    this.dashArray = 0; // Will be calculated
    this.dashGap = 0; // Will be calculated
    this.offset = 0; // Will be set based on measurements
    this.speed = 0.5; // pixels per frame
    this.resizeTimeout = null;
    this.handleResize = this.handleResize.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);

    if (
      !this.container ||
      !this.measureElement ||
      !this.pathElement ||
      !this.textPathElement
    ) {
      return;
    }

    // Check for reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    this.init();
  }

  /**
   * Calculate and apply marquee positioning values
   * Extracted to shared method to ensure consistency between init and resize
   */
  calculateAndApplyValues() {
    // Measure the text width
    const textWidth = this.measureElement.offsetWidth;
    if (!textWidth) {
      return false;
    }

    // Use the same ratios as handleResize (which works correctly)
    const GAP_RATIO = 0.1;
    const DASH_ARRAY_RATIO = 0.963;
    const INITIAL_OFFSET_RATIO = 1.03;

    this.segmentLength = textWidth * (1 + GAP_RATIO);
    this.dashArray = textWidth * DASH_ARRAY_RATIO;
    this.dashGap = this.segmentLength - this.dashArray;

    // Update path dash array
    this.pathElement.setAttribute(
      'stroke-dasharray',
      `${this.dashArray} ${this.dashGap}`,
    );

    // Reposition tspans with calculated segment length
    this.tspans.forEach((tspan, index) => {
      const xOffset = index * this.segmentLength;
      tspan.setAttribute('x', xOffset);
    });

    // Set initial offset
    this.offset = this.dashArray * INITIAL_OFFSET_RATIO;
    this.pathElement.setAttribute('stroke-dashoffset', this.offset);
    this.textPathElement.setAttribute('startOffset', `-${this.offset}`);

    return true;
  }

  init() {
    // Wait for fonts to load and DOM to be ready
    // Use requestAnimationFrame to ensure layout is complete
    requestAnimationFrame(() => {
      // Try to calculate values, retry if fonts aren't loaded yet
      if (!this.calculateAndApplyValues()) {
        // If measurement failed, wait for fonts to load
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => {
            requestAnimationFrame(() => {
              if (this.calculateAndApplyValues()) {
                this.animate();
              }
            });
          });
        } else {
          // Fallback: retry after a short delay
          setTimeout(() => {
            requestAnimationFrame(() => {
              if (this.calculateAndApplyValues()) {
                this.animate();
              }
            });
          }, 100);
        }
        return;
      }

      // Start animation
      this.animate();
    });

    // Listen for window resize to recalculate values
    window.addEventListener('resize', this.handleResize);

    // Listen for hover to pause/resume animation
    this.container.addEventListener('mouseenter', this.handleMouseEnter);
    this.container.addEventListener('mouseleave', this.handleMouseLeave);
  }

  handleResize() {
    // Debounce resize handler
    const RESIZE_DEBOUNCE_DELAY = 150; // milliseconds
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      // Recalculate all values using shared method
      this.calculateAndApplyValues();
    }, RESIZE_DEBOUNCE_DELAY);
  }

  handleMouseEnter() {
    this.isPaused = true;
  }

  handleMouseLeave() {
    this.isPaused = false;
  }

  animate() {
    const updateAnimation = () => {
      if (this.isPaused) {
        this.animationId = requestAnimationFrame(updateAnimation);
        return;
      }

      // Update offset (scrolls along the path)
      // Increase offset to move text forward along the path
      this.offset += this.speed;

      // Loop seamlessly when we've scrolled one segment length
      // Reset to create seamless loop
      const dashTotal = this.dashArray + this.dashGap;
      if (this.offset >= dashTotal) {
        this.offset = 0;
      }

      // Update both stroke-dashoffset and startOffset together (synchronized)
      this.pathElement.setAttribute('stroke-dashoffset', this.offset);
      this.textPathElement.setAttribute('startOffset', `-${this.offset}`);

      this.animationId = requestAnimationFrame(updateAnimation);
    };

    this.animationId = requestAnimationFrame(updateAnimation);
  }

  onSectionUnload() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    // Clean up resize listener
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }

    // Clean up hover listeners
    if (this.container) {
      this.container.removeEventListener('mouseenter', this.handleMouseEnter);
      this.container.removeEventListener('mouseleave', this.handleMouseLeave);
    }
  }
}
